import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { useUserProfile } from '@/hooks/account/useUser';

interface OnboardingGateProps {
  children: ReactNode;
}

/**
 * Redirects first-time users to `/onboarding` before they can reach the app.
 *
 * Sits inside `ProtectedRoute` (auth is already guaranteed) and wraps the
 * `AppLayout` routes. Reads the backend profile once — React Query caches it,
 * so this is effectively free on subsequent routes.
 *
 * Fail-open by design: we ONLY redirect when we positively know the user is
 * NOT onboarded (`onboarded === false`). While the profile is still loading (or
 * on a load error / missing field) we render the app rather than a blocking
 * full-screen spinner — so a slow `/me` request can never hang the whole
 * screen. An already-onboarded user briefly sees the app (its own components
 * handle their loading); a not-yet-onboarded user sees a brief flash before the
 * redirect. Both are better than a spinner that can stall.
 */
export function OnboardingGate({ children }: OnboardingGateProps) {
  const { data: profile } = useUserProfile();

  if (profile?.onboarded === false) {
    return <Navigate to='/onboarding' replace />;
  }

  return <>{children}</>;
}
